import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Hexagon, Users, Building2, DollarSign, Shield, Menu,
  Power, PowerOff, Eye, EyeOff, Save, Search, Settings, Key, Mail, BarChart3,
  Activity, Globe, CreditCard, AlertTriangle, Download, Database, FileText, CalendarClock, Bell, Trash2, Zap,
  LifeBuoy, Send, ArrowLeft, LogIn, Plus, UserCog, MessageSquare, BellRing
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { superadminApi, emailApi, apiErrorMessage } from '../lib/api';
import CompanyLogo from '../components/CompanyLogo';
import EmailTemplatesManager from '../components/EmailTemplatesManager';
import UiMessagesManager from '../components/UiMessagesManager';
import ApprovalRemindersManager from '../components/ApprovalRemindersManager';
import RequestRoutingManager from '../components/RequestRoutingManager';
import PublicHolidaysManager from '../components/PublicHolidaysManager';
import EmploymentLawPanel from '../components/EmploymentLawPanel';
import SignupBlocksCard from '../components/SignupBlocksCard';
import RolloverMonitor from '../components/RolloverMonitor';
import NotificationsMonitor from '../components/NotificationsMonitor';
import DeleteTenantDialog from '../components/DeleteTenantDialog';
import PurgeNowDialog from '../components/PurgeNowDialog';
import SsoProviderCard from '../components/SsoProviderCard';
import InterestList from '../components/InterestList';
import AttachmentPicker, { filesFromPaste, filterFiles } from '../components/AttachmentPicker';
import AttachmentList from '../components/AttachmentList';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import SuperadminSidebar, { buildSuperadminNav } from '../components/SuperadminSidebar';
import { format } from 'date-fns';
import { MONTHLY, ANNUAL } from '../lib/pricing';

// Data Export Section Component
const DataExportSection = ({ tenants }) => {
  const [exporting, setExporting] = useState(false);
  const [exportingTenant, setExportingTenant] = useState(null);

  const handleFullExport = async () => {
    setExporting(true);
    try {
      const response = await superadminApi.exportFullDatabase();
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'holidayhive_backup.json';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Full database export downloaded successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export database');
    } finally {
      setExporting(false);
    }
  };

  const handleTenantExport = async (tenantId, companyName) => {
    setExportingTenant(tenantId);
    try {
      // Phase D: full customer backup as a readable Excel workbook
      // (Organisation / Departments / Users / Leave / Work Schedules)
      const response = await superadminApi.exportTenant(tenantId);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const safeName = companyName.replace(/\s+/g, '-');
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `${safeName}-full-backup-${timestamp}.xlsx`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export for ${companyName} downloaded`);
    } catch (error) {
      console.error('Tenant export failed:', error);
      toast.error('Failed to export tenant data');
    } finally {
      setExportingTenant(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Full Database Export */}
      <div className="card-hive p-6 border-red-500/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <Database className="w-6 h-6 text-red-500" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-heading font-bold mb-1">Full Database Export</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Download a complete backup of all tenants, users, departments, leave data, and settings as a JSON file.
              This can be used to restore data or migrate to a new system.
            </p>
            <Button 
              onClick={handleFullExport}
              disabled={exporting}
              className="bg-red-500 hover:bg-red-600"
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Full Backup
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Per-Tenant Export */}
      <div className="card-hive p-6 border-red-500/20">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-6 h-6 text-amber-500" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-bold mb-1">Export Individual Tenant</h3>
            <p className="text-sm text-muted-foreground">
              Download a customer's full backup as an Excel workbook — organisation, departments,
              people with allowances, all leave, and work schedules. Ideal for handing a customer
              their own data (and the same file the delete flow offers).
            </p>
          </div>
        </div>

        {tenants.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No customer tenants found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tenants.map((tenant) => (
              <div 
                key={tenant.id}
                className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${tenant.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />
                  <div>
                    <p className="font-medium">{tenant.company_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {tenant.user_count} user{tenant.user_count !== 1 ? 's' : ''} • 
                      Created {format(new Date(tenant.created_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTenantExport(tenant.id, tenant.company_name)}
                  disabled={exportingTenant === tenant.id}
                >
                  {exportingTenant === tenant.id ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Export Info */}
      <div className="card-hive p-4 bg-blue-500/5 border-blue-500/20">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-500 mb-1">About Data Exports</p>
            <ul className="text-muted-foreground space-y-1">
              <li>• Exports are in JSON format and can be opened in any text editor</li>
              <li>• Passwords are NOT included in exports (hashed passwords are excluded)</li>
              <li>• Exports include: tenants, users, departments, locations, leave types, leave requests, and allowances</li>
              <li>• For complete backups, also use Supabase's built-in backup features</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const SuperadminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, startImpersonation } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [pipeline, setPipeline] = useState(null);   // CRM Phase 3
  const [churned, setChurned] = useState([]);        // former customers
  const [showChurned, setShowChurned] = useState(false);
  const [followUps, setFollowUps] = useState([]);    // "who to contact today" (1c)
  const [supportTickets, setSupportTickets] = useState([]);   // support console
  const [supportSelected, setSupportSelected] = useState(null);
  const [supportFilter, setSupportFilter] = useState('');
  const [supportReply, setSupportReply] = useState('');
  const [supportReplyFiles, setSupportReplyFiles] = useState([]);
  const [supportOpenCount, setSupportOpenCount] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [teamForm, setTeamForm] = useState({ first_name: '', last_name: '', email: '', work_phone: '', is_owner: false });
  const [capsCatalog, setCapsCatalog] = useState([]);
  const [editRightsFor, setEditRightsFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [activeSection, setActiveSection] = useState('customers');
  // Trials panel
  const [trials, setTrials] = useState([]);
  const [trialDays, setTrialDays] = useState(14);
  const [trialDaysInput, setTrialDaysInput] = useState('14');
  const [savingTrialDays, setSavingTrialDays] = useState(false);
  const [trialSort, setTrialSort] = useState({ key: 'days_left', dir: 'asc' });
  const [deleteTenantFor, setDeleteTenantFor] = useState(null); // Phase D
  const [purgeTenantFor, setPurgeTenantFor] = useState(null);   // the nuke
  
  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    company_name: 'HolidayHive Ltd',
    company_tagline: 'Staff Holidays, Simplified',
    logo_url: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
    email: 'hello@holidayhive.co.uk',
    phone: '',
    website: 'https://holidayhive.co.uk',
    company_number: '',
    vat_number: '',
    vat_rate: 20,
    bank_name: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_sort_code: '',
    bank_iban: '',
    bank_swift: '',
    invoice_prefix: 'INV',
    invoice_footer_text: '',
    payment_terms_days: 14,
    // Site URLs - used for email links etc.
    site_url: 'https://www.holidayhive.co.uk'
  });
  const [savingCompany, setSavingCompany] = useState(false);

  // Email config panel state
  const [emailStatus, setEmailStatus] = useState(null);
  const [testRecipient, setTestRecipient] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const handleSendTestEmail = async () => {
    if (!testRecipient) { toast.error('Enter a recipient email address'); return; }
    setSendingTest(true);
    try {
      const res = await emailApi.sendTest(testRecipient);
      toast.success(res.data?.message || 'Test email sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  // Editable email config state
  const [emailForm, setEmailForm] = useState({ sender_email: '', sender_name: '', primary_provider: 'resend', resend_api_key: '', sendgrid_api_key: '' });
  const [revealResend, setRevealResend] = useState(false);
  const [revealSendgrid, setRevealSendgrid] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  const handleRevealKey = async (provider) => {
    try {
      const res = await emailApi.revealKey(provider);
      const key = res.data?.key || '';
      if (provider === 'resend') { setEmailForm(p => ({ ...p, resend_api_key: key })); setRevealResend(true); }
      else { setEmailForm(p => ({ ...p, sendgrid_api_key: key })); setRevealSendgrid(true); }
      if (!key) toast.info(`No ${provider} key stored yet`);
    } catch (e) {
      toast.error('Failed to reveal key');
    }
  };

  const handleSaveEmailConfig = async () => {
    setSavingEmail(true);
    try {
      const payload = {
        sender_email: emailForm.sender_email || null,
        sender_name: emailForm.sender_name || null,
        primary_provider: emailForm.primary_provider || null,
      };
      if (emailForm.resend_api_key !== '') payload.resend_api_key = emailForm.resend_api_key;
      if (emailForm.sendgrid_api_key !== '') payload.sendgrid_api_key = emailForm.sendgrid_api_key;
      const res = await emailApi.saveConfig(payload);
      setEmailStatus(res.data);
      toast.success('Email settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSavingEmail(false);
    }
  };

  // Address lookup (Royal Mail / Loqate) key state
  const [addressForm, setAddressForm] = useState({ key: '' });
  const [revealAddress, setRevealAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressConfigured, setAddressConfigured] = useState(false);

  // Sign-in providers (Google + Microsoft) — Superadmin → API Keys
  //
  // ⚠️ Loaded when the section OPENS, not once in fetchData(), and deliberately so. The
  // `source` field ('database' / 'environment' / 'none') can change without anybody touching
  // this page — someone edits the server's `.env`, or a deploy ships a new one — and a stale
  // "Configured" pill is exactly the confusion this whole feature exists to remove.
  const [ssoConfig, setSsoConfig] = useState(null);
  const loadSsoConfig = async () => {
    try {
      const res = await superadminApi.getSsoConfig();
      setSsoConfig(res.data || null);
    } catch (e) {
      // Say so. A silent failure here would render both cards as "Not set", which reads as
      // "your keys are gone" when in fact we just could not ask.
      console.error('Sign-in provider config failed to load:', e);
      toast.error(apiErrorMessage(e, 'Could not load the sign-in provider keys'));
    }
  };

  // Payment provider config (Stripe + GoCardless) — superadmin only
  const [paymentsStatus, setPaymentsStatus] = useState(null);
  const [savingPayments, setSavingPayments] = useState(false);
  const [testingPayments, setTestingPayments] = useState(false);
  const [paymentsTest, setPaymentsTest] = useState(null);
  const [payForm, setPayForm] = useState({
    stripe_enabled: false, stripe_mode: 'test',
    stripe_test_secret_key: '', stripe_test_publishable_key: '', stripe_test_webhook_secret: '',
    stripe_live_secret_key: '', stripe_live_publishable_key: '', stripe_live_webhook_secret: '',
    gocardless_enabled: false, gocardless_mode: 'sandbox',
    gocardless_sandbox_token: '', gocardless_sandbox_webhook_secret: '',
    gocardless_live_token: '', gocardless_live_webhook_secret: '',
  });
  const setPay = (k, v) => setPayForm((p) => ({ ...p, [k]: v }));

  const handleRevealPaymentsKey = async (provider, mode, field, formKey) => {
    try {
      const res = await superadminApi.revealPaymentsKey(provider, mode, field);
      setPay(formKey, res.data?.key || '');
      if (!res.data?.key) toast.info('No key stored yet for this slot');
    } catch (e) {
      toast.error('Failed to reveal key');
    }
  };

  const handleSavePayments = async () => {
    setSavingPayments(true);
    try {
      const p = payForm;
      const payload = {
        stripe_enabled: p.stripe_enabled, stripe_mode: p.stripe_mode,
        gocardless_enabled: p.gocardless_enabled, gocardless_mode: p.gocardless_mode,
      };
      ['stripe_test_secret_key', 'stripe_test_publishable_key', 'stripe_test_webhook_secret',
       'stripe_live_secret_key', 'stripe_live_publishable_key', 'stripe_live_webhook_secret',
       'gocardless_sandbox_token', 'gocardless_sandbox_webhook_secret',
       'gocardless_live_token', 'gocardless_live_webhook_secret',
      ].forEach((k) => { if (p[k] !== '') payload[k] = p[k]; });
      const res = await superadminApi.savePaymentsConfig(payload);
      setPaymentsStatus(res.data);
      // Clear typed secrets so the masked values show again.
      setPayForm((prev) => ({ ...prev,
        stripe_test_secret_key: '', stripe_test_webhook_secret: '',
        stripe_live_secret_key: '', stripe_live_webhook_secret: '',
        gocardless_sandbox_token: '', gocardless_sandbox_webhook_secret: '',
        gocardless_live_token: '', gocardless_live_webhook_secret: '' }));
      toast.success('Payment settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save payment settings');
    } finally {
      setSavingPayments(false);
    }
  };

  const handleTestPayments = async () => {
    setTestingPayments(true); setPaymentsTest(null);
    try {
      const res = await superadminApi.testPaymentsConnection();
      setPaymentsTest(res.data);
    } catch (e) {
      setPaymentsTest({ ok: false, message: e.response?.data?.detail || 'Test failed' });
    } finally {
      setTestingPayments(false);
    }
  };

  const [syncingPlans, setSyncingPlans] = useState(false);
  const handleSyncPlans = async () => {
    setSyncingPlans(true);
    try {
      const res = await superadminApi.syncStripePlans();
      toast.success(res.data?.message || 'Stripe plans synced');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to sync plans');
    } finally {
      setSyncingPlans(false);
    }
  };

  const handleRevealAddress = async () => {
    try {
      const res = await superadminApi.getAddressApiKey();
      const key = res.data?.key || '';
      setAddressForm({ key });
      setRevealAddress(true);
      if (!key) toast.info('No address-lookup key stored yet');
    } catch (e) { toast.error('Failed to reveal key'); }
  };

  const handleSaveAddress = async () => {
    if (addressForm.key === '') { toast.info('Enter a key to save'); return; }
    setSavingAddress(true);
    try {
      await superadminApi.updatePlatformSetting('royal_mail_api_key', addressForm.key);
      setAddressConfigured(true);
      setRevealAddress(false);
      setAddressForm({ key: '' });
      toast.success('Address lookup key saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save key');
    } finally { setSavingAddress(false); }
  };

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      toast.error('Superadmin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, tenantsRes, companyRes, emailRes, addressRes, paymentsRes] = await Promise.all([
        superadminApi.getStats().catch(() => ({ data: null })),
        // ⚠️ THIS `.catch()` HID A 500 FOR A DAY. One customer with no company name yet made
        // the endpoint fail Pydantic validation, and this quietly turned that into an empty
        // list — so the page showed "No customers found" directly beneath a card reading
        // "Total Customers 12", with nothing in the console (Rob spotted the contradiction,
        // 17/08/2026). Still don't let it break the whole page, but SAY SO.
        superadminApi.listTenants().catch((e) => {
          console.error('Customer list failed to load:', e);
          toast.error("Couldn't load the customer list — the numbers above may be right while "
                      + 'the list below is empty. Check the backend log.');
          return { data: [] };
        }),
        superadminApi.getSystemSettings().catch(() => ({ data: null })),
        emailApi.getStatus().catch(() => ({ data: null })),
        superadminApi.getAddressApiKey().catch(() => ({ data: null })),
        superadminApi.getPaymentsConfig().catch(() => ({ data: null })),
      ]);
      setAddressConfigured(!!(addressRes && addressRes.data && addressRes.data.key));
      if (paymentsRes?.data) {
        setPaymentsStatus(paymentsRes.data);
        const pd = paymentsRes.data;
        setPayForm((prev) => ({ ...prev,
          stripe_enabled: !!pd.stripe?.enabled,
          stripe_mode: pd.stripe?.mode || 'test',
          stripe_test_publishable_key: pd.stripe?.test?.publishable_key || '',
          stripe_live_publishable_key: pd.stripe?.live?.publishable_key || '',
          gocardless_enabled: !!pd.gocardless?.enabled,
          gocardless_mode: pd.gocardless?.mode || 'sandbox',
        }));
      }
      if (emailRes?.data) {
        setEmailStatus(emailRes.data);
        setEmailForm(prev => ({
          ...prev,
          sender_email: emailRes.data.sender_email || '',
          sender_name: emailRes.data.sender_name || '',
          primary_provider: emailRes.data.primary_provider || 'resend',
        }));
      }
      
      if (statsRes?.data) setStats(statsRes.data);
      // Filter out the HolidayHive Admin tenant
      setTenants((tenantsRes?.data || []).filter(t => t.company_name !== 'HolidayHive Admin'));
      // CRM Phase 3: sales pipeline tile + former customers (best-effort).
      superadminApi.getCrmPipeline().then((r) => setPipeline(r.data)).catch(() => {});
      superadminApi.listChurnedCustomers().then((r) => setChurned(r.data || [])).catch(() => {});
      // Cockpit 1c: "who to contact today" (best-effort).
      superadminApi.getDueFollowUps().then((r) => setFollowUps(r.data || [])).catch(() => {});
      
      if (companyRes.data) {
        setCompanySettings(prev => ({ ...prev, ...companyRes.data }));
      }
    } catch (error) {
      // Individual calls are best-effort (a limited superadmin may lack some
      // areas); only a genuine auth failure should surface. Never bounce a
      // superadmin out of their own panel — the nav already hides what they can't use.
      console.error('Failed to fetch some admin data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveCompanySettings = async () => {
    setSavingCompany(true);
    try {
      await superadminApi.updateSystemSettings(companySettings);
      toast.success('Company details saved successfully');
    } catch (error) {
      console.error('Failed to save company settings:', error);
      toast.error('Failed to save company details');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleTenantAction = async (tenantId, action) => {
    // Phase D: 'delete' goes through DeleteTenantDialog, never through here.
    const actionText = action === 'activate' ? 'activate' : 'suspend';
    if (!confirm(`Are you sure you want to ${actionText} this tenant?`)) return;

    try {
      const res = await superadminApi.actionTenant(tenantId, action);
      // Backend message matters here — 'activate' on a deletion-scheduled
      // tenant says the deletion was cancelled.
      toast.success(res.data?.message || `Tenant ${action}d successfully`);
      fetchData();
    } catch (error) {
      console.error('Failed to action tenant:', error);
      toast.error('Failed to update tenant');
    }
  };

  // Superadmin navigation — the ONE list, imported from the sidebar component.
  // This page used to keep its own copy, which drifted twice (Trials, then Email
  // Reminders): the item appeared here but never in the actual sidebar. Only used
  // now to work out which section to open by default.
  const adminNavItems = buildSuperadminNav(user);

  // Capability gating (Phase 2): owners see all; others only their granted areas.
  const superCaps = user?.platform_capabilities || [];
  const isOwnerSA = !!user?.is_platform_owner;
  const SECTION_CAP = {
    customers: 'customers', trials: 'customers', analytics: 'analytics', billing: 'billing', company: 'company',
    email: 'email', emailtemplates: 'email', messages: 'messages', rollover: 'rollover', notifications: 'notifications',
    api: 'api_keys', settings: 'platform_settings', activity: 'activity_log',
    export: 'data_export', support: 'support',
  };
  const canSeeSection = (section) => {
    if (isOwnerSA) return true;
    const cap = SECTION_CAP[section];
    return !cap || superCaps.includes(cap);
  };
  useEffect(() => {   // land a limited superadmin on a section they can actually see
    if (user && !canSeeSection(activeSection)) {
      const first = adminNavItems.find((i) => canSeeSection(i.section));
      if (first) setActiveSection(first.section);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Support console (tickets)
  const loadSupport = async () => {
    try {
      const res = await superadminApi.listSupportTickets(supportFilter || undefined);
      setSupportTickets(res.data || []);
    } catch (e) { /* best-effort */ }
  };
  const refreshSupportCount = () => {
    superadminApi.listSupportTickets()
      .then((r) => setSupportOpenCount((r.data || []).filter((t) => t.status !== 'resolved').length))
      .catch(() => {});
  };
  useEffect(() => {
    if (activeSection === 'support') loadSupport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, supportFilter]);
  const openSupportTicket = async (id) => {
    try { const res = await superadminApi.getSupportTicket(id); setSupportSelected(res.data); }
    catch (e) { toast.error('Could not open the ticket'); }
  };
  const sendSupportReply = async () => {
    if ((!supportReply.trim() && supportReplyFiles.length === 0) || !supportSelected) return;
    try {
      const res = await superadminApi.replySupportTicket(supportSelected.id, supportReply.trim(), supportReplyFiles);
      setSupportReply(''); setSupportReplyFiles([]); setSupportSelected(res.data); loadSupport(); refreshSupportCount();
    } catch (e) { toast.error(apiErrorMessage(e, 'Could not send the reply')); }
  };
  const setSupportStatus = async (status) => {
    if (!supportSelected) return;
    try {
      const res = await superadminApi.setSupportTicketStatus(supportSelected.id, status);
      setSupportSelected(res.data); loadSupport(); refreshSupportCount();
    } catch (e) { toast.error('Could not update the status'); }
  };
  const loginAsUser = async () => {
    if (!supportSelected) return;
    try {
      const res = await superadminApi.impersonate(supportSelected.id);
      const { token, user: u, company } = res.data;
      startImpersonation(token, { name: u.name, email: u.email, company, ticketId: supportSelected.id });
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start the support session');
    }
  };
  const revokeSupportAccess = async () => {
    if (!supportSelected) return;
    try {
      const res = await superadminApi.revokeSupportAccess(supportSelected.id);
      setSupportSelected(res.data); loadSupport();
      toast.success('Account access turned off');
    } catch (e) {
      toast.error('Could not turn off access');
    }
  };
  const SUPPORT_STATUS = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved' };
  const SUPPORT_CLS = {
    open: 'bg-amber-500/15 text-amber-600',
    in_progress: 'bg-blue-500/15 text-blue-500',
    resolved: 'bg-green-500/15 text-green-600',
  };
  useEffect(() => {   // deep-link from the cockpit: ?section=support&ticket=<id>
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    const ticket = params.get('ticket');
    if (section) setActiveSection(section);
    if (ticket) openSupportTicket(ticket);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  useEffect(() => {   // open-ticket count for the nav badge
    superadminApi.listSupportTickets()
      .then((r) => setSupportOpenCount((r.data || []).filter((t) => t.status !== 'resolved').length))
      .catch(() => {});
  }, []);

  // Platform team (owner-only)
  const loadTeam = () => {
    superadminApi.listTeam().then((r) => setTeamMembers(r.data || [])).catch(() => {});
    superadminApi.listTeamCapabilities().then((r) => setCapsCatalog(r.data || [])).catch(() => {});
  };
  const capLabel = (key) => capsCatalog.find((c) => c.key === key)?.label || key;
  const toggleMemberCap = async (m, capKey, checked) => {
    const next = checked ? [...m.capabilities, capKey] : m.capabilities.filter((c) => c !== capKey);
    try { await superadminApi.updateTeamMember(m.id, { capabilities: next }); loadTeam(); }
    catch (e) { toast.error('Could not update rights'); }
  };
  useEffect(() => {
    if (activeSection === 'team') loadTeam();
  }, [activeSection]);
  useEffect(() => {
    if (activeSection === 'api') loadSsoConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);
  const createTeamMember = async () => {
    if (!teamForm.first_name.trim() || !teamForm.email.trim()) return;
    try {
      await superadminApi.createTeamMember(teamForm);
      setTeamForm({ first_name: '', last_name: '', email: '', work_phone: '', is_owner: false });
      setShowAddTeam(false); loadTeam();
      toast.success('Superadmin added — emailed a set-password link');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add superadmin'); }
  };
  const toggleTeamOwner = async (m) => {
    try { await superadminApi.updateTeamMember(m.id, { is_owner: !m.is_owner }); loadTeam(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };
  const removeTeamMember = async (m) => {
    if (!window.confirm(`Remove ${m.first_name} ${m.last_name} as a superadmin?`)) return;
    try { await superadminApi.deleteTeamMember(m.id); loadTeam(); toast.success('Superadmin removed'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); }
  };

  // ---- Trials panel ----
  const loadTrials = async () => {
    try {
      const res = await superadminApi.getTrials();
      setTrials(res.data.trials || []);
      setTrialDays(res.data.trial_period_days || 14);
      setTrialDaysInput(String(res.data.trial_period_days || 14));
    } catch (e) { toast.error(apiErrorMessage(e, 'Could not load trials')); }
  };
  useEffect(() => {
    if (activeSection === 'trials') loadTrials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);
  const saveTrialDays = async () => {
    const n = parseInt(trialDaysInput, 10);
    if (!Number.isFinite(n) || n < 1 || n > 90) { toast.error('Enter a whole number of days between 1 and 90'); return; }
    setSavingTrialDays(true);
    try {
      const res = await superadminApi.updateTrialSettings(n);
      setTrialDays(res.data.trial_period_days);
      setTrialDaysInput(String(res.data.trial_period_days));
      toast.success(`New sign-ups now get a ${res.data.trial_period_days}-day trial`);
    } catch (e) { toast.error(apiErrorMessage(e, 'Could not save the trial length')); }
    finally { setSavingTrialDays(false); }
  };
  const handleExtendTrial = async (id, days) => {
    try { await superadminApi.extendTrial(id, days); toast.success(`Trial extended by ${days} days`); loadTrials(); }
    catch (e) { toast.error(apiErrorMessage(e, 'Could not extend the trial')); }
  };
  const handleEndTrial = async (row) => {
    if (!window.confirm(`End ${row.company_name}'s free trial now? They'll drop onto the paywall. You can extend them again later.`)) return;
    try { await superadminApi.endTrial(row.tenant_id); toast.success('Trial ended'); loadTrials(); }
    catch (e) { toast.error(apiErrorMessage(e, 'Could not end the trial')); }
  };
  const sortTrialsBy = (key) => setTrialSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const trialFee = (r) => (r.plan === 'annual' ? r.annual_fee : r.monthly_fee);
  const sortedTrials = [...trials].sort((a, b) => {
    const { key, dir } = trialSort;
    let av = key === 'fee' ? trialFee(a) : a[key];
    let bv = key === 'fee' ? trialFee(b) : b[key];
    if (av === null || av === undefined) av = '';
    if (bv === null || bv === undefined) bv = '';
    let cmp;
    if (typeof av === 'string' && typeof bv === 'string') cmp = av.localeCompare(bv);
    else cmp = av > bv ? 1 : av < bv ? -1 : 0;
    return dir === 'asc' ? cmp : -cmp;
  });
  const trialArrow = (key) => (trialSort.key === key ? (trialSort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  const filteredTenants = tenants.filter(t => {
    // ⚠️ `|| ''` BECAUSE A COMPANY NAME CAN BE NULL. Sign-up creates the account and /welcome
    // asks for the name afterwards, so anybody who stopped in between is a real customer with
    // no name yet. `null.toLowerCase()` would throw and take the whole list down with it.
    const matchesSearch = (t.company_name || '').toLowerCase().includes(search.toLowerCase());
    // Sales CRM Phase 2 segments (auto-derived stage / group), plus the
    // legacy active/inactive and a "follow-up due" chase list.
    let matchesFilter = true;
    if (filter === 'active') matchesFilter = t.is_active;
    else if (filter === 'inactive') matchesFilter = !t.is_active;
    else if (filter === 'healthy') matchesFilter = t.stage_group === 'healthy';
    else if (filter === 'at_risk') matchesFilter = t.stage_group === 'at_risk';
    else if (filter === 'lost') matchesFilter = t.stage_group === 'lost';
    else if (filter === 'trial_expired') matchesFilter = t.stage === 'trial_expired';
    else if (filter === 'follow_up') matchesFilter = t.follow_up_due;
    return matchesSearch && matchesFilter;
  });

  // Stage badge styling — grouped by colour so "at-risk" and "lost" read
  // differently at a glance.
  const STAGE_META = {
    trialing:        { label: 'Trialing',        cls: 'bg-blue-500/15 text-blue-500' },
    active:          { label: 'Active',          cls: 'bg-green-500/15 text-green-600' },
    trial_ending:    { label: 'Trial ending',    cls: 'bg-amber-500/15 text-amber-600' },
    suspended:       { label: 'Suspended',       cls: 'bg-amber-500/15 text-amber-600' },
    payment_failing: { label: 'Payment failing', cls: 'bg-amber-500/15 text-amber-600' },
    trial_expired:   { label: 'Trial expired',   cls: 'bg-red-500/15 text-red-500' },
    closing:         { label: 'Closing',         cls: 'bg-red-500/15 text-red-500' },
    churned:         { label: 'Churned',         cls: 'bg-red-500/15 text-red-500' },
  };

  if (user?.role !== 'superadmin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      <DeleteTenantDialog
        open={!!deleteTenantFor}
        onOpenChange={(o) => { if (!o) setDeleteTenantFor(null); }}
        tenant={deleteTenantFor}
        onSuccess={fetchData}
      />
      <PurgeNowDialog
        open={!!purgeTenantFor}
        onOpenChange={(o) => { if (!o) setPurgeTenantFor(null); }}
        tenant={purgeTenantFor}
        onSuccess={fetchData}
      />
      
      {/* Admin Sidebar (shared with the customer detail page) */}
      <SuperadminSidebar
        activeSection={activeSection}
        onSectionClick={setActiveSection}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        supportOpenCount={supportOpenCount}
      />

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl font-bold">
                  {activeSection === 'customers' && 'Customer Management'}
                  {activeSection === 'interest' && 'Interest list'}
                  {activeSection === 'trials' && 'Free Trials'}
                  {activeSection === 'analytics' && 'Platform Analytics'}
                  {activeSection === 'billing' && 'Billing & Revenue'}
                  {activeSection === 'company' && 'HolidayHive Company Details'}
                  {activeSection === 'email' && 'Email Configuration'}
                  {activeSection === 'emailtemplates' && 'Email Templates'}
                  {activeSection === 'messages' && 'App Messages'}
                  {activeSection === 'approvalreminders' && 'Approval Reminders'}
                  {activeSection === 'rollover' && 'Leave-Year Rollover'}
                  {activeSection === 'notifications' && 'Scheduled Notifications'}
                  {activeSection === 'api' && 'API Management'}
                  {activeSection === 'settings' && 'Platform Settings'}
                  {activeSection === 'support' && 'Support'}
                  {activeSection === 'team' && 'Platform Team'}
                  {activeSection === 'export' && 'Data Export & Backup'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {activeSection === 'customers' && 'Manage all HolidayHive customers'}
                  {activeSection === 'interest' && 'People who registered on the pre-launch page at holidayhive.co.uk'}
                  {activeSection === 'trials' && 'Set the trial length and manage companies on trial'}
                  {activeSection === 'analytics' && 'View platform-wide statistics'}
                  {activeSection === 'billing' && 'Revenue and subscription management'}
                  {activeSection === 'company' && 'Company info, logo, bank details for invoices'}
                  {activeSection === 'email' && 'Configure email providers'}
                  {activeSection === 'emailtemplates' && 'Edit the emails HolidayHive sends'}
                  {activeSection === 'messages' && 'Customise the in-app toast messages'}
                  {activeSection === 'rollover' && 'Monitor the automatic year-end rollover scheduler'}
                  {activeSection === 'notifications' && 'Monitor digests & birthday reminders'}
                  {activeSection === 'api' && 'Manage API keys and access'}
                  {activeSection === 'settings' && 'Configure platform settings'}
                  {activeSection === 'support' && 'Customer support tickets'}
                  {activeSection === 'team' && 'Manage superadmin accounts and owners'}
                  {activeSection === 'export' && 'Download database backups as JSON'}
                </p>
              </div>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="p-6">
            {/* Customers Section */}
            {activeSection === 'customers' && (
              <div className="space-y-6">
                {/* Quick Stats */}
                {stats && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="card-hive p-4 border-red-500/20">
                      <div className="flex items-center gap-3 mb-2">
                        <Building2 className="w-5 h-5 text-red-500" />
                        <span className="text-sm text-muted-foreground">Total Customers</span>
                      </div>
                      {/* No more "- 1" fudges: the backend excludes the
                          platform tenant/superadmin itself (17 Jul) */}
                      <p className="text-3xl font-heading font-bold">{stats.total_tenants}</p>
                      <p className="text-xs text-muted-foreground">{stats.active_tenants} active</p>
                    </div>
                    
                    <div className="card-hive p-4 border-red-500/20">
                      <div className="flex items-center gap-3 mb-2">
                        <Users className="w-5 h-5 text-blue-500" />
                        <span className="text-sm text-muted-foreground">Total Users</span>
                      </div>
                      <p className="text-3xl font-heading font-bold">{stats.total_users}</p>
                    </div>
                    
                    <div className="card-hive p-4 border-red-500/20">
                      <div className="flex items-center gap-3 mb-2">
                        <Activity className="w-5 h-5 text-yellow-500" />
                        <span className="text-sm text-muted-foreground">Leave Requests</span>
                      </div>
                      <p className="text-3xl font-heading font-bold">{stats.total_leave_requests}</p>
                      <p className="text-xs text-yellow-500">{stats.pending_requests} pending</p>
                    </div>
                    
                    <div className="card-hive p-4 border-red-500/20">
                      <div className="flex items-center gap-3 mb-2">
                        <DollarSign className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-muted-foreground">Monthly Revenue</span>
                      </div>
                      <p className="text-3xl font-heading font-bold text-green-500">£{stats.monthly_revenue}</p>
                      <p className="text-xs text-muted-foreground">{stats.total_users} × {MONTHLY}</p>
                    </div>
                  </div>
                )}

                {/* CRM pipeline tile (Phase 3) — click a card to filter the list */}
                {pipeline && (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="card-hive p-4">
                      <span className="text-sm text-muted-foreground">Trial conversion</span>
                      <p className="text-3xl font-heading font-bold text-green-600">
                        {pipeline.conversion_rate === null || pipeline.conversion_rate === undefined
                          ? 'n/a' : `${pipeline.conversion_rate}%`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {pipeline.converted} converted · {pipeline.trials_not_converted} didn't
                      </p>
                    </div>
                    <button type="button" onClick={() => setFilter('at_risk')}
                            className="card-hive p-4 text-left hover:ring-2 hover:ring-amber-500/40 transition">
                      <span className="text-sm text-muted-foreground">At-risk</span>
                      <p className="text-3xl font-heading font-bold text-amber-600">{pipeline.at_risk_total}</p>
                      <p className="text-xs text-muted-foreground">needs attention →</p>
                    </button>
                    <button type="button" onClick={() => setFilter('lost')}
                            className="card-hive p-4 text-left hover:ring-2 hover:ring-red-500/40 transition">
                      <span className="text-sm text-muted-foreground">Lost / closing</span>
                      <p className="text-3xl font-heading font-bold text-red-500">{pipeline.lost_total}</p>
                      <p className="text-xs text-muted-foreground">{pipeline.closing_now} in grace →</p>
                    </button>
                    <button type="button" onClick={() => setShowChurned(v => !v)}
                            className="card-hive p-4 text-left hover:ring-2 hover:ring-red-500/40 transition">
                      <span className="text-sm text-muted-foreground">Churned (gone)</span>
                      <p className="text-3xl font-heading font-bold">{pipeline.churned_total}</p>
                      <p className="text-xs text-muted-foreground">{showChurned ? 'hide' : 'view'} former customers →</p>
                    </button>
                  </div>
                )}

                {/* Cockpit 1c: who to contact today (+ why) */}
                {followUps.length > 0 && (
                  <div className="card-hive overflow-hidden">
                    <div className="p-4 border-b border-border flex items-center gap-2">
                      <CalendarClock className="w-5 h-5 text-amber-500" />
                      <h2 className="font-heading font-bold text-lg">Follow-ups due ({followUps.length})</h2>
                    </div>
                    <div className="divide-y divide-border">
                      {followUps.map((f) => (
                        <button
                          key={f.customer_id}
                          type="button"
                          onClick={() => navigate(`/superadmin/customers/${f.customer_id}`)}
                          className="w-full text-left p-4 hover:bg-secondary/30 transition flex items-start justify-between gap-4"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{f.company_name}</span>
                              {f.overdue_days > 0 ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-500">
                                  {f.overdue_days} day{f.overdue_days !== 1 ? 's' : ''} overdue
                                </span>
                              ) : (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600">Due today</span>
                              )}
                              {f.do_not_contact && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-500">Do not contact</span>
                              )}
                            </div>
                            {f.latest_note ? (
                              <p className="text-sm text-muted-foreground mt-1 truncate">{f.latest_note}</p>
                            ) : (
                              <p className="text-sm text-muted-foreground mt-1 italic">No note yet — open to add context.</p>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(f.follow_up_at).toLocaleDateString('en-GB')}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Former customers (from churn_records — their tenants are gone) */}
                {showChurned && (
                  <div className="card-hive overflow-hidden border-red-500/20">
                    <div className="p-4 border-b border-border">
                      <h2 className="font-heading font-bold text-lg">Former customers</h2>
                      <p className="text-sm text-muted-foreground">Permanently deleted — kept for sales history only.</p>
                    </div>
                    {churned.length === 0 ? (
                      <p className="p-6 text-sm text-muted-foreground text-center">No former customers yet.</p>
                    ) : (
                      <div className="divide-y divide-border">
                        {churned.map((c) => (
                          <div key={c.id} className="p-4 flex items-center justify-between gap-4">
                            <div>
                              <p className="font-medium">{c.company_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {c.seats_at_close || 0} seat{c.seats_at_close === 1 ? '' : 's'} ·
                                {c.closed_by === 'customer' ? ' left us' : ' removed by us'}
                                {c.reason ? ` · ${c.reason.replace(/_/g, ' ')}` : ''}
                              </p>
                            </div>
                            <div className="text-right text-xs text-muted-foreground">
                              {c.admin_contact_email && !c.marketing_opt_out && (
                                <a href={`mailto:${c.admin_contact_email}`} className="text-primary hover:underline">
                                  {c.admin_contact_email}
                                </a>
                              )}
                              {c.marketing_opt_out && <span className="italic">opted out</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Customer List */}
                <div className="card-hive overflow-hidden border-red-500/20">
                  <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                    <h2 className="font-heading font-bold text-lg">All Customers</h2>
                    <div className="flex gap-3 flex-wrap">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search companies..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="pl-9 w-48"
                        />
                      </div>
                      <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                      >
                        <option value="all">All customers</option>
                        <option value="healthy">Healthy</option>
                        <option value="at_risk">At-risk</option>
                        <option value="lost">Lost / closing</option>
                        <option value="trial_expired">Trials not converted</option>
                        <option value="follow_up">Follow-up due</option>
                        <option value="active">Active (login)</option>
                        <option value="inactive">Suspended (login)</option>
                      </select>
                    </div>
                  </div>
                  
                  {filteredTenants.length === 0 ? (
                    <div className="p-8 text-center">
                      <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No customers found</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {filteredTenants.map((tenant) => (
                        <div 
                          key={tenant.id}
                          className="p-4 hover:bg-secondary/30 transition-colors cursor-pointer"
                          onClick={() => navigate(`/superadmin/customers/${tenant.id}`)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-4 w-80 shrink-0 min-w-0">
                              <CompanyLogo
                                domain={tenant.domain}
                                size={40}
                                rounded="rounded-lg"
                                bg={tenant.is_active ? 'bg-green-500/20' : 'bg-red-500/20'}
                                iconColor={tenant.is_active ? 'text-green-500' : 'text-red-500'}
                              />
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {/* ⚠️ A sign-up that stopped at /welcome has no name yet. Say
                                      that plainly rather than showing an empty row — it is a
                                      lead worth chasing, not a broken record. */}
                                  <p className="font-medium truncate">
                                    {tenant.company_name || (
                                      <span className="italic text-muted-foreground">
                                        Sign-up not finished
                                      </span>
                                    )}
                                  </p>
                                  {tenant.stage && STAGE_META[tenant.stage] && (
                                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${STAGE_META[tenant.stage].cls}`}>
                                      {STAGE_META[tenant.stage].label}
                                    </span>
                                  )}
                                  {tenant.follow_up_due && (
                                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                                      Follow-up due
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {tenant.user_count} user{tenant.user_count !== 1 ? 's' : ''} • £{tenant.monthly_cost}/month
                                </p>
                              </div>
                            </div>

                            {/* Billing at-a-glance columns (wide screens only) */}
                            <div className="hidden lg:flex items-center gap-5 text-sm whitespace-nowrap shrink-0">
                              <div className="w-32">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">On trial</p>
                                {tenant.on_trial ? (
                                  <p className="font-medium text-amber-500">
                                    Yes{tenant.trial_ends_at ? ` · ${new Date(tenant.trial_ends_at).toLocaleDateString('en-GB')}` : ''}
                                  </p>
                                ) : (
                                  <p className="font-medium text-muted-foreground">No</p>
                                )}
                              </div>
                              <div className="w-32">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Payment</p>
                                <p className="font-medium">{tenant.payment_provider || '—'}</p>
                              </div>
                              <div className="w-16">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Plan</p>
                                <p className="font-medium">{tenant.plan || '—'}</p>
                              </div>
                              <div className="w-24">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Next due</p>
                                <p className="font-medium">{tenant.next_payment_at ? new Date(tenant.next_payment_at).toLocaleDateString('en-GB') : '—'}</p>
                              </div>
                              <div className="w-20 text-right">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Amount</p>
                                <p className="font-medium">{tenant.next_payment_amount != null ? `£${Number(tenant.next_payment_amount).toFixed(2)}` : '—'}</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3 ml-auto shrink-0" onClick={(e) => e.stopPropagation()}>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                tenant.is_active 
                                  ? 'bg-green-500/20 text-green-500' 
                                  : 'bg-red-500/20 text-red-500'
                              }`}>
                                {tenant.subscription_status}
                              </span>
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/superadmin/customers/${tenant.id}`)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              
                              {tenant.is_active ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-500 hover:bg-red-500/10"
                                  onClick={() => handleTenantAction(tenant.id, 'deactivate')}
                                >
                                  <PowerOff className="w-4 h-4 mr-1" />
                                  Suspend
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-green-500 hover:text-green-500 hover:bg-green-500/10"
                                  onClick={() => handleTenantAction(tenant.id, 'activate')}
                                >
                                  <Power className="w-4 h-4 mr-1" />
                                  {tenant.deletion_requested_at ? 'Cancel deletion' : 'Activate'}
                                </Button>
                              )}
                              {/* Phase D: real deletion, via the grace flow */}
                              {!tenant.deletion_requested_at && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-500 hover:bg-red-500/10"
                                  onClick={() => setDeleteTenantFor(tenant)}
                                  data-testid={`delete-tenant-${tenant.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete…
                                </Button>
                              )}
                              {tenant.deletion_requested_at && (
                                <>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-500 font-medium self-center">
                                    Purges {(() => { const d = new Date(tenant.deletion_requested_at); d.setDate(d.getDate() + 30); return d.toLocaleDateString('en-GB'); })()}
                                  </span>
                                  {/* The nuke: skip the grace — only offered once already in it */}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-500 hover:bg-red-500/10"
                                    onClick={() => setPurgeTenantFor(tenant)}
                                    data-testid={`purge-now-${tenant.id}`}
                                  >
                                    <Zap className="w-4 h-4 mr-1" />
                                    Purge now…
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Analytics Section */}
            {activeSection === 'analytics' && (
              <div className="space-y-6">
                <div className="card-hive p-8 text-center border-red-500/20">
                  <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-2">Platform Analytics</h3>
                  <p className="text-muted-foreground">
                    Detailed analytics dashboard coming soon.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Will include: user growth, leave trends, customer retention, and more.
                  </p>
                </div>
              </div>
            )}

            {/* Billing Section */}
            {activeSection === 'billing' && (
              <div className="space-y-6">
                {stats && (
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="card-hive p-6 border-green-500/20">
                      <DollarSign className="w-8 h-8 text-green-500 mb-3" />
                      <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                      <p className="text-4xl font-heading font-bold text-green-500">£{stats.monthly_revenue - 1}</p>
                    </div>
                    <div className="card-hive p-6 border-blue-500/20">
                      <Users className="w-8 h-8 text-blue-500 mb-3" />
                      <p className="text-sm text-muted-foreground">Billable Users</p>
                      <p className="text-4xl font-heading font-bold">{stats.total_users - 1}</p>
                    </div>
                    <div className="card-hive p-6 border-primary/20">
                      <Globe className="w-8 h-8 text-primary mb-3" />
                      <p className="text-sm text-muted-foreground">Price per User</p>
                      <p className="text-4xl font-heading font-bold">{MONTHLY}</p>
                      <p className="text-xs text-muted-foreground mt-1">a month, or {ANNUAL} a year</p>
                    </div>
                  </div>
                )}
                <div className="card-hive p-8 text-center border-red-500/20">
                  <CreditCard className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-2">Stripe Integration</h3>
                  <p className="text-muted-foreground">
                    Payment processing integration coming soon.
                  </p>
                </div>
              </div>
            )}

            {/* Company Details Section */}
            {activeSection === 'company' && (
              <div className="space-y-6">
                {/* Company Identity */}
                <div className="card-hive p-6 border-primary/20">
                  <div className="flex items-center gap-3 mb-6">
                    <Hexagon className="w-6 h-6 text-primary" />
                    <h3 className="font-heading font-bold text-lg">Company Identity</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Company Name</label>
                      <Input
                        value={companySettings.company_name}
                        onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Tagline</label>
                      <Input
                        value={companySettings.company_tagline || ''}
                        onChange={(e) => setCompanySettings({...companySettings, company_tagline: e.target.value})}
                        placeholder="Staff Holidays, Simplified"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Website</label>
                      <Input
                        value={companySettings.website || ''}
                        onChange={(e) => setCompanySettings({...companySettings, website: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Contact Email</label>
                      <Input
                        value={companySettings.email || ''}
                        onChange={(e) => setCompanySettings({...companySettings, email: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Phone</label>
                      <Input
                        value={companySettings.phone || ''}
                        onChange={(e) => setCompanySettings({...companySettings, phone: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Company Address */}
                <div className="card-hive p-6 border-primary/20">
                  <div className="flex items-center gap-3 mb-6">
                    <Building2 className="w-6 h-6 text-primary" />
                    <h3 className="font-heading font-bold text-lg">Registered Address</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="text-sm text-muted-foreground">Address Line 1</label>
                      <Input
                        value={companySettings.address_line_1 || ''}
                        onChange={(e) => setCompanySettings({...companySettings, address_line_1: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-muted-foreground">Address Line 2</label>
                      <Input
                        value={companySettings.address_line_2 || ''}
                        onChange={(e) => setCompanySettings({...companySettings, address_line_2: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">City</label>
                      <Input
                        value={companySettings.city || ''}
                        onChange={(e) => setCompanySettings({...companySettings, city: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">County</label>
                      <Input
                        value={companySettings.county || ''}
                        onChange={(e) => setCompanySettings({...companySettings, county: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Postcode</label>
                      <Input
                        value={companySettings.postcode || ''}
                        onChange={(e) => setCompanySettings({...companySettings, postcode: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Country</label>
                      <Input
                        value={companySettings.country || ''}
                        onChange={(e) => setCompanySettings({...companySettings, country: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Legal & Tax */}
                <div className="card-hive p-6 border-primary/20">
                  <div className="flex items-center gap-3 mb-6">
                    <Shield className="w-6 h-6 text-primary" />
                    <h3 className="font-heading font-bold text-lg">Legal & Tax Details</h3>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Company Number</label>
                      <Input
                        value={companySettings.company_number || ''}
                        onChange={(e) => setCompanySettings({...companySettings, company_number: e.target.value})}
                        placeholder="e.g. 12345678"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">VAT Number</label>
                      <Input
                        value={companySettings.vat_number || ''}
                        onChange={(e) => setCompanySettings({...companySettings, vat_number: e.target.value})}
                        placeholder="e.g. GB 123456789"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">VAT Rate (%)</label>
                      <Input
                        type="number"
                        value={companySettings.vat_rate || 20}
                        onChange={(e) => setCompanySettings({...companySettings, vat_rate: parseFloat(e.target.value)})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Bank Details */}
                <div className="card-hive p-6 border-green-500/20">
                  <div className="flex items-center gap-3 mb-6">
                    <CreditCard className="w-6 h-6 text-green-500" />
                    <h3 className="font-heading font-bold text-lg">Bank Details (for Invoices)</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Bank Name</label>
                      <Input
                        value={companySettings.bank_name || ''}
                        onChange={(e) => setCompanySettings({...companySettings, bank_name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Account Name</label>
                      <Input
                        value={companySettings.bank_account_name || ''}
                        onChange={(e) => setCompanySettings({...companySettings, bank_account_name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Account Number</label>
                      <Input
                        value={companySettings.bank_account_number || ''}
                        onChange={(e) => setCompanySettings({...companySettings, bank_account_number: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Sort Code</label>
                      <Input
                        value={companySettings.bank_sort_code || ''}
                        onChange={(e) => setCompanySettings({...companySettings, bank_sort_code: e.target.value})}
                        placeholder="XX-XX-XX"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">IBAN (Optional)</label>
                      <Input
                        value={companySettings.bank_iban || ''}
                        onChange={(e) => setCompanySettings({...companySettings, bank_iban: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">SWIFT/BIC (Optional)</label>
                      <Input
                        value={companySettings.bank_swift || ''}
                        onChange={(e) => setCompanySettings({...companySettings, bank_swift: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Invoice Settings */}
                <div className="card-hive p-6 border-primary/20">
                  <div className="flex items-center gap-3 mb-6">
                    <Settings className="w-6 h-6 text-primary" />
                    <h3 className="font-heading font-bold text-lg">Invoice Settings</h3>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Invoice Number Prefix</label>
                      <Input
                        value={companySettings.invoice_prefix || 'INV'}
                        onChange={(e) => setCompanySettings({...companySettings, invoice_prefix: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Payment Terms (Days)</label>
                      <Input
                        type="number"
                        value={companySettings.payment_terms_days || 14}
                        onChange={(e) => setCompanySettings({...companySettings, payment_terms_days: parseInt(e.target.value)})}
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-muted-foreground">Invoice Footer Text</label>
                      <textarea
                        value={companySettings.invoice_footer_text || ''}
                        onChange={(e) => setCompanySettings({...companySettings, invoice_footer_text: e.target.value})}
                        className="w-full mt-1 p-3 rounded-md border border-input bg-background text-sm min-h-[80px]"
                        placeholder="Any additional text to appear at the bottom of invoices..."
                      />
                    </div>
                  </div>
                </div>

                {/* Site URLs */}
                <div className="card-hive p-6 border-blue-500/20">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-blue-500" />
                    <h3 className="font-heading font-bold text-lg">Site URLs</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Configure the application URL. This is used for password reset emails, invitation links, and other system-generated URLs.
                    Update this when moving between sandbox/preview and production environments.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-muted-foreground">Application URL</label>
                      <Input
                        value={companySettings.site_url || ''}
                        onChange={(e) => setCompanySettings({...companySettings, site_url: e.target.value})}
                        placeholder="https://www.holidayhive.co.uk"
                        className="mt-1"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Used in password reset emails and invitation links. Include https:// but no trailing slash.
                      </p>
                    </div>
                    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        <strong>Important:</strong> When deploying to production, update this URL to your production domain. 
                        For sandbox/preview testing, use the preview URL.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button onClick={handleSaveCompanySettings} disabled={savingCompany} className="bg-primary">
                    {savingCompany ? 'Saving...' : 'Save Company Details'}
                  </Button>
                </div>
              </div>
            )}

            {/* Email Config Section */}
            {activeSection === 'email' && (
              <div className="space-y-6">
                {/* Overall status */}
                <div className="card-hive p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Mail className="w-6 h-6 text-primary" />
                    <h3 className="font-heading font-bold text-lg">Email Service</h3>
                    {emailStatus && (
                      <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${emailStatus.configured ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                        {emailStatus.configured ? 'Active' : 'Not configured'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {emailStatus ? emailStatus.message : 'Loading email status…'}
                  </p>
                </div>

                {/* Editable configuration */}
                <div className="card-hive p-6">
                  <h3 className="font-heading font-bold text-lg mb-4">Provider Configuration</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-xs text-muted-foreground uppercase">Sender email</label>
                      <Input
                        type="email"
                        value={emailForm.sender_email}
                        onChange={(e) => setEmailForm(p => ({ ...p, sender_email: e.target.value }))}
                        placeholder="noreply@yourdomain.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase">Sender name</label>
                      <Input
                        value={emailForm.sender_name}
                        onChange={(e) => setEmailForm(p => ({ ...p, sender_name: e.target.value }))}
                        placeholder="HolidayHive"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-xs text-muted-foreground uppercase">Primary provider</label>
                    <select
                      value={emailForm.primary_provider}
                      onChange={(e) => setEmailForm(p => ({ ...p, primary_provider: e.target.value }))}
                      className="mt-1 w-full h-10 px-3 rounded-md bg-input border border-border text-sm"
                    >
                      <option value="resend">Resend</option>
                      <option value="sendgrid">SendGrid</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">The other configured provider is used automatically as fallback.</p>
                  </div>

                  {/* Resend key */}
                  <div className="mb-4">
                    <label className="text-xs text-muted-foreground uppercase flex items-center gap-2">
                      Resend API key
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${emailStatus?.resend_configured ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                        {emailStatus?.resend_configured ? 'Configured' : 'Not set'}
                      </span>
                    </label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type={revealResend ? 'text' : 'password'}
                        value={emailForm.resend_api_key}
                        onChange={(e) => { setEmailForm(p => ({ ...p, resend_api_key: e.target.value })); setRevealResend(true); }}
                        placeholder={emailStatus?.resend_configured ? '•••••••• (leave blank to keep current)' : 'Enter Resend API key'}
                        className="flex-1 font-mono"
                      />
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          if (revealResend) { setRevealResend(false); setEmailForm(p => ({ ...p, resend_api_key: '' })); }
                          else { handleRevealKey('resend'); }
                        }}
                        title={revealResend ? 'Hide key' : 'Reveal stored key'}
                      >
                        {revealResend ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* SendGrid key */}
                  <div className="mb-4">
                    <label className="text-xs text-muted-foreground uppercase flex items-center gap-2">
                      SendGrid API key
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${emailStatus?.sendgrid_configured ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                        {emailStatus?.sendgrid_configured ? 'Configured' : 'Not set'}
                      </span>
                    </label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type={revealSendgrid ? 'text' : 'password'}
                        value={emailForm.sendgrid_api_key}
                        onChange={(e) => { setEmailForm(p => ({ ...p, sendgrid_api_key: e.target.value })); setRevealSendgrid(true); }}
                        placeholder={emailStatus?.sendgrid_configured ? '•••••••• (leave blank to keep current)' : 'Enter SendGrid API key (optional)'}
                        className="flex-1 font-mono"
                      />
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          if (revealSendgrid) { setRevealSendgrid(false); setEmailForm(p => ({ ...p, sendgrid_api_key: '' })); }
                          else { handleRevealKey('sendgrid'); }
                        }}
                        title={revealSendgrid ? 'Hide key' : 'Reveal stored key'}
                      >
                        {revealSendgrid ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleSaveEmailConfig} disabled={savingEmail}>
                      <Save className="w-4 h-4 mr-2" />
                      {savingEmail ? 'Saving…' : 'Save settings'}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">Keys are encrypted at rest. Changes apply immediately — no restart needed.</p>
                </div>

                {/* Test send */}
                <div className="card-hive p-6">
                  <h3 className="font-heading font-bold text-lg mb-2">Send a test email</h3>
                  <p className="text-sm text-muted-foreground mb-4">Verify delivery by sending a test message to any address.</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      type="email"
                      placeholder="recipient@example.com"
                      value={testRecipient}
                      onChange={(e) => setTestRecipient(e.target.value)}
                      className="flex-1"
                    />
                    <Button onClick={handleSendTestEmail} disabled={sendingTest || !emailStatus?.configured}>
                      {sendingTest ? 'Sending…' : 'Send test'}
                    </Button>
                  </div>
                  {emailStatus && !emailStatus.configured && (
                    <p className="text-xs text-yellow-500 mt-2">No provider configured — add a key above to enable sending.</p>
                  )}
                </div>
              </div>
            )}

            {activeSection === 'emailtemplates' && (
              <EmailTemplatesManager />
            )}

            {activeSection === 'messages' && (
              <UiMessagesManager />
            )}

            {activeSection === 'approvalreminders' && (
              <ApprovalRemindersManager />
            )}

            {activeSection === 'requestrouting' && (
              <RequestRoutingManager />
            )}

            {activeSection === 'publicholidays' && (
              <PublicHolidaysManager />
            )}

            {activeSection === 'employmentlaw' && (
              <EmploymentLawPanel />
            )}

            {activeSection === 'rollover' && (
              <RolloverMonitor />
            )}

            {activeSection === 'notifications' && (
              <NotificationsMonitor />
            )}

            {activeSection === 'interest' && (
              <InterestList />
            )}

            {/* Support Section */}
            {activeSection === 'support' && (
              supportSelected ? (
                <div className="card-hive p-6 max-w-3xl">
                  <button
                    onClick={() => setSupportSelected(null)}
                    className="text-sm text-muted-foreground flex items-center gap-1 mb-4 hover:text-foreground"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to tickets
                  </button>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h2 className="font-heading font-bold text-lg">{supportSelected.subject}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${SUPPORT_CLS[supportSelected.status] || ''}`}>
                      {SUPPORT_STATUS[supportSelected.status] || supportSelected.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">
                    {supportSelected.company_name} · opened by {supportSelected.opened_by_name || 'a user'}
                  </p>

                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className="text-xs text-muted-foreground">Set status:</span>
                    {['open', 'in_progress', 'resolved'].map((s) => (
                      <button
                        key={s}
                        onClick={() => setSupportStatus(s)}
                        className={`text-xs px-2 py-1 rounded-full border transition ${
                          supportSelected.status === s ? SUPPORT_CLS[s] + ' border-transparent' : 'border-border text-muted-foreground hover:bg-secondary/40'
                        }`}
                      >
                        {SUPPORT_STATUS[s]}
                      </button>
                    ))}
                  </div>

                  {supportSelected.access_consent && (
                    <div className="mb-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={loginAsUser}
                          className="text-sm px-3 py-2 rounded-lg bg-red-500/10 text-red-500 border border-red-500/30 hover:bg-red-500/20 transition flex items-center gap-2"
                        >
                          <LogIn className="w-4 h-4" /> Log in as {supportSelected.opened_by_name || 'this user'}
                        </button>
                        <button
                          onClick={revokeSupportAccess}
                          className="text-sm px-3 py-2 rounded-lg border border-border text-muted-foreground hover:bg-secondary/40 transition"
                        >
                          Turn off access
                        </button>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        This user allowed support access. Logging in acts as them for 60 minutes; they're
                        emailed and it's logged. Access clears automatically when you resolve the ticket.
                      </p>
                    </div>
                  )}

                  <div className="space-y-3 mb-4">
                    {(supportSelected.messages || []).map((m) => (
                      <div key={m.id} className={`p-3 rounded-lg ${m.from_staff ? 'bg-primary/10' : 'bg-secondary/40'}`}>
                        {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                        <AttachmentList attachments={m.attachments} fetchBlob={superadminApi.getSupportAttachment} />
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {m.from_staff ? (m.author_name || 'Support') + ' (staff)' : (m.author_name || 'Customer')}
                          {m.created_at ? ` · ${new Date(m.created_at).toLocaleString('en-GB')}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <textarea
                        value={supportReply}
                        onChange={(e) => setSupportReply(e.target.value)}
                        onPaste={(e) => {
                          const pasted = filesFromPaste(e);
                          if (!pasted.length) return;
                          const accepted = filterFiles(pasted, supportReplyFiles);
                          if (accepted.length) setSupportReplyFiles([...supportReplyFiles, ...accepted]);
                        }}
                        rows={5}
                        placeholder="Reply to the customer… (paste a screenshot to attach it)"
                        className="input-hive w-full resize-y text-sm min-h-[120px]"
                      />
                      <AttachmentPicker files={supportReplyFiles} setFiles={setSupportReplyFiles} />
                    </div>
                    <Button size="sm" onClick={sendSupportReply} disabled={!supportReply.trim() && supportReplyFiles.length === 0}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="card-hive overflow-hidden">
                  <div className="p-4 border-b border-border flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-muted-foreground">Filter:</span>
                    {[['', 'All'], ['open', 'Open'], ['in_progress', 'In progress'], ['resolved', 'Resolved']].map(([val, label]) => (
                      <button
                        key={val || 'all'}
                        onClick={() => setSupportFilter(val)}
                        className={`text-xs px-2 py-1 rounded-full transition ${
                          supportFilter === val ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-secondary/40'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {supportTickets.length === 0 ? (
                    <p className="p-6 text-sm text-muted-foreground text-center">No tickets{supportFilter ? ' in this status' : ' yet'}.</p>
                  ) : (
                    <div className="divide-y divide-border">
                      {supportTickets.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => openSupportTicket(t.id)}
                          className="w-full text-left p-4 hover:bg-secondary/30 transition flex items-center justify-between gap-4"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">{t.subject}</p>
                              {t.access_consent && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-600 whitespace-nowrap shrink-0">
                                  Access on
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              {t.company_name} · {t.opened_by_name || 'a user'} · {t.message_count} message{t.message_count !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <div className="text-right whitespace-nowrap">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${SUPPORT_CLS[t.status] || ''}`}>
                              {SUPPORT_STATUS[t.status] || t.status}
                            </span>
                            <p className="text-[11px] text-muted-foreground mt-1">
                              {t.last_message_at ? new Date(t.last_message_at).toLocaleDateString('en-GB') : ''}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}

            {/* Platform Team Section (owner-only) */}
            {activeSection === 'team' && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <Button size="sm" onClick={() => setShowAddTeam((v) => !v)}>
                    <Plus className="w-4 h-4 mr-2" /> Add superadmin
                  </Button>
                </div>

                {showAddTeam && (
                  <div className="card-hive p-4">
                    <h3 className="font-heading font-bold mb-3">Add a superadmin</h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Input placeholder="First name" value={teamForm.first_name}
                             onChange={(e) => setTeamForm({ ...teamForm, first_name: e.target.value })} />
                      <Input placeholder="Last name" value={teamForm.last_name}
                             onChange={(e) => setTeamForm({ ...teamForm, last_name: e.target.value })} />
                      <Input placeholder="Email" value={teamForm.email}
                             onChange={(e) => setTeamForm({ ...teamForm, email: e.target.value })} />
                      <Input placeholder="Phone (optional)" value={teamForm.work_phone}
                             onChange={(e) => setTeamForm({ ...teamForm, work_phone: e.target.value })} />
                    </div>
                    <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                      <input type="checkbox" checked={teamForm.is_owner}
                             onChange={(e) => setTeamForm({ ...teamForm, is_owner: e.target.checked })} />
                      <span>Make them an owner (can manage the platform team)</span>
                    </label>
                    <div className="flex justify-end gap-2 mt-3">
                      <Button variant="outline" size="sm" onClick={() => setShowAddTeam(false)}>Cancel</Button>
                      <Button size="sm" onClick={createTeamMember}
                              disabled={!teamForm.first_name.trim() || !teamForm.email.trim()}>
                        Add + email set-password link
                      </Button>
                    </div>
                  </div>
                )}

                <div className="card-hive overflow-hidden">
                  <div className="divide-y divide-border">
                    {teamMembers.map((m) => (
                      <div key={m.id} className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center text-red-500 text-xs font-bold shrink-0">
                              {`${m.first_name?.[0] || ''}${m.last_name?.[0] || ''}`.toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{m.first_name} {m.last_name}{m.is_self ? ' (you)' : ''}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {m.email}{m.work_phone ? ` · ${m.work_phone}` : ''} · last login {m.last_login_at ? new Date(m.last_login_at).toLocaleDateString('en-GB') : 'never'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => toggleTeamOwner(m)}
                              className={`text-xs px-2.5 py-1 rounded-full border transition ${
                                m.is_owner ? 'bg-red-500/15 text-red-500 border-red-500/30' : 'border-border text-muted-foreground hover:bg-secondary/40'
                              }`}
                              title="Toggle owner"
                            >
                              {m.is_owner ? 'Owner' : 'Member'}
                            </button>
                            {!m.is_self && (
                              <button onClick={() => removeTeamMember(m)} className="text-muted-foreground hover:text-red-500" title="Remove">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="mt-2 sm:pl-13">
                          {m.is_owner ? (
                            <p className="text-xs text-muted-foreground">Full access (owner)</p>
                          ) : (
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {m.capabilities.length === 0 ? (
                                  <span className="text-xs text-muted-foreground">No rights granted</span>
                                ) : (
                                  m.capabilities.map((c) => (
                                    <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">{capLabel(c)}</span>
                                  ))
                                )}
                                <button onClick={() => setEditRightsFor(editRightsFor === m.id ? null : m.id)} className="text-xs text-red-500 hover:underline">
                                  {editRightsFor === m.id ? 'Done' : 'Edit rights'}
                                </button>
                              </div>
                              {editRightsFor === m.id && (
                                <div className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                                  {capsCatalog.map((cap) => (
                                    <label key={cap.key} className="flex items-center gap-2 text-xs cursor-pointer">
                                      <input type="checkbox" checked={m.capabilities.includes(cap.key)}
                                             onChange={(e) => toggleMemberCap(m, cap.key, e.target.checked)} />
                                      <span>{cap.label}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* API Section */}
            {activeSection === 'api' && (
              <div className="space-y-6">
                {/* Sign-in providers. ⚠️ Rendered only once the config has arrived — the cards
                    seed their Client ID box from props on mount, so showing them empty and
                    then filling them in would either flicker or, worse, let Rob press Save on
                    a blank field and clear a working key. */}
                {ssoConfig && (
                  <>
                    <SsoProviderCard
                      provider="google"
                      label="Google"
                      blurb="The Continue with Google button on the sign-in and sign-up screens."
                      secretHint="GOCSPX-..."
                      config={ssoConfig.google}
                      onSaved={(c) => c && setSsoConfig(c)}
                    />
                    <SsoProviderCard
                      provider="microsoft"
                      label="Microsoft"
                      blurb="The Continue with Microsoft button on the sign-in and sign-up screens."
                      secretHint="Paste the secret VALUE, not the Secret ID"
                      config={ssoConfig.microsoft}
                      onSaved={(c) => c && setSsoConfig(c)}
                    />
                    {/*
                      * ⚠️ **A THIRD SET OF GOOGLE KEYS, AND IT IS NOT A DUPLICATE OF THE
                      * FIRST CARD.** Calendar access is a *sensitive* scope, and Google's
                      * own Audience page says the resulting 100-user cap "applies over the
                      * entire lifetime of the project, and it cannot be reset or changed".
                      * Putting a calendar scope on the sign-in project would begin spending
                      * that allowance on the project every customer's sign-in depends on —
                      * so this is a separate Google Cloud project and a separate client.
                      * Rob's decision A, 21/08/2026.
                      *
                      * ⚠️ **DELIBERATELY THE OPPOSITE OF MICROSOFT, WHICH REUSES ITS ONE
                      * REGISTRATION FOR BOTH.** Microsoft has no equivalent penalty. Same
                      * feature, opposite decision, and the difference is not an oversight.
                      *
                      * ⚠️ Its Test button answers amber, not green or red, and that is
                      * correct — see the note in `routers/superadmin.py`. These keys have no
                      * sign-in endpoint to be tried against, and probing them through the
                      * sign-in callback would come back `redirect_uri_mismatch` and be
                      * reported as a bad secret.
                      */}
                    <SsoProviderCard
                      provider="google_calendar"
                      label="Google Calendar"
                      title="Google Calendar access"
                      blurb="Writing approved leave into people's own Google calendars. A SEPARATE Google Cloud project from the sign-in one above — see SSO-SETUP.md."
                      secretHint="GOCSPX-..."
                      config={ssoConfig.google_calendar}
                      onSaved={(c) => c && setSsoConfig(c)}
                    />
                    {/*
                      * ⚠️ **A FOURTH SET OF KEYS, AND ITS OWN APP REGISTRATION AGAIN.**
                      * DESIGN-teams.md decision K. The sign-in registration already carries
                      * sign-in for every customer AND Microsoft calendar connections;
                      * adding the bot would put three shipped features behind one secret,
                      * where one rotation takes all three down together.
                      *
                      * ⚠️ The tenant id box is OURS, not a customer's — the Azure Bot is
                      * Single Tenant (Microsoft removed the Multi Tenant option for new
                      * bots in July 2025), so tokens are minted against the tenant the
                      * registration lives in. One fixed value for the whole installation.
                      *
                      * ⚠️ Its Test button answers amber, like the calendar one — there is
                      * no sign-in endpoint to try these against.
                      */}
                    <SsoProviderCard
                      provider="teams_bot"
                      label="Microsoft Teams"
                      title="Microsoft Teams bot"
                      blurb="Sending leave requests to approve, and the daily who's-off post. A SEPARATE app registration from sign-in — see SSO-SETUP.md."
                      secretHint="Paste the secret VALUE, not the Secret ID"
                      extraLabel="Directory (tenant) ID"
                      extraHint="The tenant the bot's app registration lives in — ours, not a customer's"
                      config={ssoConfig.teams_bot}
                      onSaved={(c) => c && setSsoConfig(c)}
                    />
                    {/*
                      * ⚠️ OUR Slack app's keys — "HolidayHive Staging" on staging — from the
                      * app's Basic Information page (SLACK-SETUP.md step 4). Each company's
                      * own connection is made by its admin pressing Add to Slack, and is
                      * nothing to do with this card.
                      */}
                    {ssoConfig.slack && (
                      <SsoProviderCard
                        provider="slack"
                        label="Slack"
                        title="Slack app"
                        blurb="Add to Slack in Settings → Integrations, approving from Slack, and the who's-off post. From the app's Basic Information page — see SLACK-SETUP.md."
                        secretHint="Basic Information → App Credentials → Client Secret"
                        extraSecretLabel="Signing secret"
                        extraSecretHint="Basic Information → App Credentials → Signing Secret"
                        config={ssoConfig.slack}
                        onSaved={(c) => c && setSsoConfig(c)}
                      />
                    )}
                  </>
                )}

                <div className="card-hive p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/15 text-blue-600 flex items-center justify-center"><Key className="w-5 h-5" /></div>
                    <div>
                      <h3 className="font-heading font-bold text-lg">Address lookup (Royal Mail / Loqate)</h3>
                      <p className="text-sm text-muted-foreground">Powers the postcode &rarr; address search on the employee and onboarding forms.</p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="text-xs text-muted-foreground uppercase flex items-center gap-2">
                      API key
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${addressConfigured ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                        {addressConfigured ? 'Configured' : 'Not set'}
                      </span>
                    </label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type={revealAddress ? 'text' : 'password'}
                        value={addressForm.key}
                        onChange={(e) => { setAddressForm({ key: e.target.value }); setRevealAddress(true); }}
                        placeholder={addressConfigured ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022 (leave blank to keep current)' : 'Enter Loqate / AddressNow key'}
                        className="flex-1 font-mono"
                      />
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => { if (revealAddress) { setRevealAddress(false); setAddressForm({ key: '' }); } else { handleRevealAddress(); } }}
                        title={revealAddress ? 'Hide key' : 'Reveal stored key'}
                      >
                        {revealAddress ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">From your Loqate / AddressNow (Royal Mail PAF) account. Paste a new key to switch accounts &mdash; it applies immediately, no restart needed.</p>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleSaveAddress} disabled={savingAddress}>
                      <Save className="w-4 h-4 mr-2" />{savingAddress ? 'Saving\u2026' : 'Save key'}
                    </Button>
                  </div>
                </div>

                {/* Stripe */}
                <div className="card-hive p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-500/15 text-indigo-600 flex items-center justify-center"><Key className="w-5 h-5" /></div>
                      <div>
                        <h3 className="font-heading font-bold text-lg">Stripe</h3>
                        <p className="text-sm text-muted-foreground">Card payments &amp; subscriptions. Secret keys are stored encrypted.</p>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={payForm.stripe_enabled} onChange={(e) => { const on = e.target.checked; setPay('stripe_enabled', on); if (on) setPay('gocardless_enabled', false); }} className="w-4 h-4 accent-primary" />
                      Enabled
                    </label>
                  </div>
                  <div className="flex items-center gap-4 mb-4 text-sm">
                    <span className="text-muted-foreground">Mode:</span>
                    {['test', 'live'].map((m) => (
                      <label key={m} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name="stripe_mode" checked={payForm.stripe_mode === m} onChange={() => setPay('stripe_mode', m)} className="accent-primary" />
                        <span className={m === 'live' ? 'font-medium text-red-600' : ''}>{m === 'live' ? 'Live' : 'Test'}</span>
                      </label>
                    ))}
                  </div>
                  {(() => {
                    const mode = payForm.stripe_mode;
                    const secretKey = `stripe_${mode}_secret_key`;
                    const pubKey = `stripe_${mode}_publishable_key`;
                    const whKey = `stripe_${mode}_webhook_secret`;
                    const st = (paymentsStatus && paymentsStatus.stripe && paymentsStatus.stripe[mode]) || {};
                    return (
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-muted-foreground uppercase">Secret key ({mode})</label>
                          <div className="flex gap-2 mt-1">
                            <Input type="password" value={payForm[secretKey]} onChange={(e) => setPay(secretKey, e.target.value)} placeholder={st.secret_key_masked ? `${st.secret_key_masked} (leave blank to keep)` : `sk_${mode}_...`} className="flex-1 font-mono" />
                            <Button variant="secondary" type="button" onClick={() => handleRevealPaymentsKey('stripe', mode, 'secret', secretKey)} title="Reveal stored key"><Eye className="w-4 h-4" /></Button>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground uppercase">Publishable key ({mode})</label>
                          <Input value={payForm[pubKey]} onChange={(e) => setPay(pubKey, e.target.value)} placeholder={st.publishable_key || `pk_${mode}_...`} className="mt-1 font-mono" />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground uppercase">Webhook signing secret ({mode})</label>
                          <div className="flex gap-2 mt-1">
                            <Input type="password" value={payForm[whKey]} onChange={(e) => setPay(whKey, e.target.value)} placeholder={st.webhook_secret_set ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022 (leave blank to keep)' : 'whsec_...'} className="flex-1 font-mono" />
                            <Button variant="secondary" type="button" onClick={() => handleRevealPaymentsKey('stripe', mode, 'webhook', whKey)} title="Reveal stored secret"><Eye className="w-4 h-4" /></Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Point your Stripe dashboard webhook at <span className="font-mono">/api/billing/webhook/stripe</span></p>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex items-center gap-3 mt-4 flex-wrap">
                    <Button variant="outline" type="button" onClick={handleTestPayments} disabled={testingPayments}>{testingPayments ? 'Testing\u2026' : 'Test connection'}</Button>
                    <Button variant="outline" type="button" onClick={handleSyncPlans} disabled={syncingPlans} title="Create/refresh the monthly & annual per-seat Prices in Stripe">{syncingPlans ? 'Syncing\u2026' : 'Sync Stripe plans'}</Button>
                    {paymentsTest && (
                      <span className={`text-sm ${paymentsTest.ok ? 'text-green-600' : 'text-red-600'}`}>{paymentsTest.message}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">"Sync Stripe plans" creates the recurring per-seat Prices (\u00a31/mo, \u00a310/yr) in the current mode \u2014 run it once before customers can subscribe.</p>
                  <p className="text-xs text-muted-foreground mt-2">When enabled, Stripe provides <strong>card</strong> and <strong>Direct Debit</strong> (Bacs). Only one provider is active at a time, so enabling Stripe turns GoCardless off.</p>
                </div>

                {/* GoCardless */}
                <div className="card-hive p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-teal-500/15 text-teal-600 flex items-center justify-center"><Key className="w-5 h-5" /></div>
                      <div>
                        <h3 className="font-heading font-bold text-lg">GoCardless</h3>
                        <p className="text-sm text-muted-foreground">Direct Debit (Bacs). Access token is stored encrypted.</p>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={payForm.gocardless_enabled} onChange={(e) => { const on = e.target.checked; setPay('gocardless_enabled', on); if (on) setPay('stripe_enabled', false); }} className="w-4 h-4 accent-primary" />
                      Enabled
                    </label>
                  </div>
                  <div className="flex items-center gap-4 mb-4 text-sm">
                    <span className="text-muted-foreground">Mode:</span>
                    {['sandbox', 'live'].map((m) => (
                      <label key={m} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" name="gocardless_mode" checked={payForm.gocardless_mode === m} onChange={() => setPay('gocardless_mode', m)} className="accent-primary" />
                        <span className={m === 'live' ? 'font-medium text-red-600' : ''}>{m === 'live' ? 'Live' : 'Sandbox'}</span>
                      </label>
                    ))}
                  </div>
                  {(() => {
                    const mode = payForm.gocardless_mode;
                    const tokKey = `gocardless_${mode}_token`;
                    const whKey = `gocardless_${mode}_webhook_secret`;
                    const gc = (paymentsStatus && paymentsStatus.gocardless && paymentsStatus.gocardless[mode]) || {};
                    return (
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-muted-foreground uppercase">Access token ({mode})</label>
                          <div className="flex gap-2 mt-1">
                            <Input type="password" value={payForm[tokKey]} onChange={(e) => setPay(tokKey, e.target.value)} placeholder={gc.token_masked ? `${gc.token_masked} (leave blank to keep)` : 'GoCardless access token'} className="flex-1 font-mono" />
                            <Button variant="secondary" type="button" onClick={() => handleRevealPaymentsKey('gocardless', mode, 'token', tokKey)} title="Reveal stored token"><Eye className="w-4 h-4" /></Button>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground uppercase">Webhook secret ({mode})</label>
                          <div className="flex gap-2 mt-1">
                            <Input type="password" value={payForm[whKey]} onChange={(e) => setPay(whKey, e.target.value)} placeholder={gc.webhook_secret_set ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022 (leave blank to keep)' : 'Webhook secret'} className="flex-1 font-mono" />
                            <Button variant="secondary" type="button" onClick={() => handleRevealPaymentsKey('gocardless', mode, 'webhook', whKey)} title="Reveal stored secret"><Eye className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  <p className="text-xs text-muted-foreground mt-3">When enabled, GoCardless provides <strong>Direct Debit only</strong> (no card), and Stripe is turned off. Only one provider is active at a time — leave this off to use Stripe for card + Direct Debit.</p>
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSavePayments} disabled={savingPayments}>
                    <Save className="w-4 h-4 mr-2" />{savingPayments ? 'Saving\u2026' : 'Save payment settings'}
                  </Button>
                </div>
              </div>
            )}

            {/* Trials Section */}
            {activeSection === 'trials' && (
              <div className="space-y-6">
                {/* Trial length for new sign-ups */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-heading font-semibold mb-1">Trial length for new sign-ups</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Applies to companies that sign up from now on. Existing trials keep their own dates — use the table below to adjust individuals. Clamped to 1–90 days.
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Input
                      type="number" min="1" max="90"
                      value={trialDaysInput}
                      onChange={(e) => setTrialDaysInput(e.target.value)}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">days</span>
                    <Button onClick={saveTrialDays} disabled={savingTrialDays || String(trialDays) === trialDaysInput.trim()}>
                      <Save className="w-4 h-4 mr-2" /> Save
                    </Button>
                    <span className="text-sm text-muted-foreground">Currently <strong>{trialDays}</strong> days</span>
                  </div>
                </div>

                {/* Bots refused at sign-up (Group A7) — the captcha trigger. */}
                <SignupBlocksCard />

                {/* Companies on trial */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-semibold">Companies on trial ({sortedTrials.length})</h3>
                    <Button variant="outline" size="sm" onClick={() => setTrialSort({ key: 'days_left', dir: 'asc' })}>
                      Reset sort
                    </Button>
                  </div>
                  {sortedTrials.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">No companies are currently on trial.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-muted-foreground border-b border-border">
                            {[
                              ['company_name', 'Company'],
                              ['employees', 'Employees'],
                              ['plan', 'Plan'],
                              ['fee', 'Fee'],
                              ['created_at', 'Signed up'],
                              ['trial_ends_at', 'Trial ends'],
                              ['days_left', 'Days left'],
                              ['signed_up', 'Paid setup?'],
                            ].map(([key, label]) => (
                              <th key={key} className="py-2 pr-4 cursor-pointer select-none hover:text-foreground whitespace-nowrap" onClick={() => sortTrialsBy(key)}>
                                {label}{trialArrow(key)}
                              </th>
                            ))}
                            <th className="py-2 text-right whitespace-nowrap">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedTrials.map((r) => (
                            <tr key={r.tenant_id} className="border-b border-border/50">
                              <td className="py-2 pr-4 font-medium">{r.company_name}</td>
                              <td className="py-2 pr-4">{r.employees}</td>
                              <td className="py-2 pr-4 capitalize">{r.plan || '—'}</td>
                              <td className="py-2 pr-4 whitespace-nowrap">
                                {r.plan === 'annual' ? `£${r.annual_fee.toFixed(2)}/yr` : `£${r.monthly_fee.toFixed(2)}/mo`}
                              </td>
                              <td className="py-2 pr-4 whitespace-nowrap">{r.created_at ? format(new Date(r.created_at), 'd MMM yyyy') : '—'}</td>
                              <td className="py-2 pr-4 whitespace-nowrap">{r.trial_ends_at ? format(new Date(r.trial_ends_at), 'd MMM yyyy') : '—'}</td>
                              <td className="py-2 pr-4">
                                {r.expired
                                  ? <span className="text-red-500 font-medium">Expired</span>
                                  : <span className={r.days_left <= 3 ? 'text-amber-500 font-medium' : ''}>{r.days_left}</span>}
                              </td>
                              <td className="py-2 pr-4">
                                {r.signed_up
                                  ? <span className="text-green-600 font-medium">Yes{r.payment_channel ? ` (${r.payment_channel === 'bacs' ? 'DD' : r.payment_channel})` : ''}</span>
                                  : <span className="text-muted-foreground">Not yet</span>}
                              </td>
                              <td className="py-2 text-right whitespace-nowrap space-x-1">
                                <Button variant="outline" size="sm" onClick={() => handleExtendTrial(r.tenant_id, 7)}>+7d</Button>
                                <Button variant="outline" size="sm" onClick={() => handleExtendTrial(r.tenant_id, 14)}>+14d</Button>
                                <Button variant="outline" size="sm" onClick={() => handleExtendTrial(r.tenant_id, 30)}>+30d</Button>
                                {r.signed_up ? (
                                  <Button variant="outline" size="sm" disabled title="They've already set up a payment method — you can't end their trial early">End</Button>
                                ) : (
                                  <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleEndTrial(r)}>End</Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Settings Section */}
            {activeSection === 'settings' && (
              <div className="space-y-6">
                <div className="card-hive p-8 text-center border-red-500/20">
                  <Settings className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-heading font-bold mb-2">Platform Settings</h3>
                  <p className="text-muted-foreground">
                    Global platform configuration coming soon.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Will include: default settings, feature flags, maintenance mode.
                  </p>
                </div>
              </div>
            )}

            {/* Data Export Section */}
            {activeSection === 'export' && (
              <DataExportSection tenants={tenants} />
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SuperadminPage;
